import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, Edit, LogOut, Calendar, Eye, EyeOff, Image, Ticket, Bell, Send, Users, Upload, Copy, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

async function uploadImage(file) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${API}/upload`, formData, {
    headers: { ...getHeaders(), "Content-Type": "multipart/form-data" }
  });
  return res.data.url;
}

function fixUrl(url) {
  if (!url) return "";
  if (url.includes("/api/uploads/")) return "/api/uploads/" + url.split("/api/uploads/").pop();
  return url;
}

// ── Sponsorship Tiers Manager Component ──
function SponsorshipManager() {
  const [tiers, setTiers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", price_range: "", description: "", benefits: "", order: 0, active: true });

  const fetchTiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sponsorship-tiers`);
      setTiers(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchTiers(); }, [fetchTiers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, benefits: form.benefits.split("\n").map(b => b.trim()).filter(Boolean) };
    try {
      if (editId) {
        await axios.put(`${API}/sponsorship-tiers/${editId}`, payload, { headers: getHeaders() });
        toast.success("Tier updated");
      } else {
        await axios.post(`${API}/sponsorship-tiers`, payload, { headers: getHeaders() });
        toast.success("Tier created");
      }
      setShowForm(false); setEditId(null);
      setForm({ name: "", price_range: "", description: "", benefits: "", order: 0, active: true });
      fetchTiers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-subheading text-2xl uppercase tracking-tight">Sponsorship Tiers</h2>
        <button
          data-testid="add-tier-btn"
          onClick={() => { setShowForm(true); setEditId(null); setForm({ name: "", price_range: "", description: "", benefits: "", order: 0, active: true }); }}
          className="flex items-center gap-2 font-subheading text-xs uppercase tracking-widest bg-white text-black px-5 py-2.5 hover:bg-zinc-200 transition-colors"
        >
          <Plus size={14} /> Add Tier
        </button>
      </div>

      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 p-8 mb-6">
          <h3 className="font-subheading text-lg uppercase tracking-wider mb-6">{editId ? "Edit Tier" : "New Tier"}</h3>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Name</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bc-input w-full" placeholder="Founding Sponsor" />
              </div>
              <div>
                <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Price Range</label>
                <input required value={form.price_range} onChange={e => setForm({...form, price_range: e.target.value})} className="bc-input w-full" placeholder="$5,000 - $10,000" />
              </div>
              <div>
                <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Order</label>
                <input type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="bc-input w-full" />
              </div>
            </div>
            <div>
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Description</label>
              <textarea rows={2} required value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bc-input w-full resize-none" placeholder="Tier description..." />
            </div>
            <div>
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Benefits (one per line)</label>
              <textarea rows={4} required value={form.benefits} onChange={e => setForm({...form, benefits: e.target.value})} className="bc-input w-full resize-none" placeholder="Logo placement on all video content&#10;Featured sponsor at all events&#10;VIP access" />
            </div>
            <div className="flex gap-4">
              <button type="submit" className="font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors">
                {editId ? "Update" : "Create"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white transition-colors">Cancel</button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="space-y-3">
        {tiers.map(tier => (
          <div key={tier.id} className="border border-white/10 p-5 flex items-center gap-4 card-hover">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h4 className="font-subheading text-sm uppercase tracking-wider">{tier.name}</h4>
                <span className="font-body text-xs text-zinc-500">{tier.price_range}</span>
              </div>
              <p className="font-body text-xs text-zinc-600 mt-1 truncate">{tier.description}</p>
              <p className="font-body text-[10px] text-zinc-700 mt-1">{tier.benefits?.length || 0} benefits | Order: {tier.order}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => {
                setForm({ name: tier.name, price_range: tier.price_range, description: tier.description, benefits: (tier.benefits || []).join("\n"), order: tier.order, active: tier.active });
                setEditId(tier.id); setShowForm(true);
              }} className="p-2 text-zinc-500 hover:text-white transition-colors"><Edit size={16} /></button>
              <button onClick={async () => {
                if (!window.confirm("Delete this tier?")) return;
                await axios.delete(`${API}/sponsorship-tiers/${tier.id}`, { headers: getHeaders() });
                toast.success("Deleted"); fetchTiers();
              }} className="p-2 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {tiers.length === 0 && <p className="font-body text-sm text-zinc-600">No sponsorship tiers yet</p>}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", image_url: "", event_date: "", popup_duration: 8, support_url: "", active: true });
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editEventId, setEditEventId] = useState(null);
  const [eventForm, setEventForm] = useState({ title: "", description: "", image_url: "", event_date: "", venue: "", ticket_types: "General Admission, VIP, Veteran (Free)", ticket_url: "", active: true, notify_subscribers: false });
  const [rsvpView, setRsvpView] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [ticketUrlInput, setTicketUrlInput] = useState("");
  const [popupEnabled, setPopupEnabled] = useState(true);
  const [pendingTickets, setPendingTickets] = useState([]);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      await axios.get(`${API}/admin/me`, { headers: getHeaders() });
    } catch {
      navigate("/admin/login");
      return;
    }
    try {
      const [promos, statsRes, eventsRes, popupRes] = await Promise.all([
        axios.get(`${API}/promotions`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/events?include_inactive=true`),
        axios.get(`${API}/settings/popup`)
      ]);
      setPromotions(promos.data);
      setStats(statsRes.data);
      setEvents(eventsRes.data);
      setPopupEnabled(popupRes.data.enabled);
      // Fetch pending tickets
      try {
        const pendRes = await axios.get(`${API}/tickets/pending`, { headers: getHeaders() });
        setPendingTickets(pendRes.data);
      } catch {}
    } catch {}
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) {
        await axios.put(`${API}/promotions/${editId}`, form, { headers: getHeaders() });
        toast.success("Promotion updated");
      } else {
        await axios.post(`${API}/promotions`, form, { headers: getHeaders() });
        toast.success("Promotion created");
      }
      setForm({ title: "", description: "", image_url: "", event_date: "", popup_duration: 8, support_url: "", active: true });
      setShowForm(false);
      setEditId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setLoading(false);
  };

  const deletePromo = async (id) => {
    if (!window.confirm("Delete this promotion?")) return;
    try {
      await axios.delete(`${API}/promotions/${id}`, { headers: getHeaders() });
      toast.success("Deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const editPromo = (promo) => {
    setForm({
      title: promo.title,
      description: promo.description,
      image_url: promo.image_url,
      event_date: promo.event_date,
      popup_duration: promo.popup_duration,
      support_url: promo.support_url || "",
      active: promo.active
    });
    setEditId(promo.id);
    setShowForm(true);
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/promotions/${promo.id}`, { active: !promo.active }, { headers: getHeaders() });
      fetchData();
    } catch {}
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  const now = new Date().toISOString().slice(0, 10);
  const upcoming = promotions.filter(p => p.event_date >= now);
  const past = promotions.filter(p => p.event_date < now);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-24 pb-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Admin</p>
            <h1 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight">Dashboard</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 font-subheading text-xs uppercase tracking-widest text-zinc-400 hover:text-white transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          {[
            { label: "Contacts", value: stats.contacts || 0 },
            { label: "Nominations", value: stats.nominations || 0 },
            { label: "Subscribers", value: stats.subscribers || 0 },
            { label: "Donors", value: stats.donors || 0 },
          ].map(s => (
            <div key={s.label} className="border border-white/10 p-6 text-center">
              <span className="font-heading text-3xl">{s.value}</span>
              <p className="font-body text-xs uppercase tracking-wider text-zinc-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Popup Toggle + Sync Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          <div className="border border-white/10 p-5 flex items-center justify-between">
            <div>
              <h3 className="font-subheading text-sm uppercase tracking-wider">Event Popup</h3>
              <p className="font-body text-xs text-zinc-500 mt-1">Show popup to new visitors</p>
            </div>
            <button
              data-testid="popup-toggle-btn"
              onClick={async () => {
                const next = !popupEnabled;
                try {
                  await axios.put(`${API}/settings/popup?enabled=${next}`, {}, { headers: getHeaders() });
                  setPopupEnabled(next);
                  toast.success(next ? "Popup enabled" : "Popup disabled");
                } catch { toast.error("Failed to update"); }
              }}
              className={`font-subheading text-xs uppercase tracking-widest px-5 py-2.5 transition-all duration-300 ${
                popupEnabled
                  ? "bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20"
                  : "bg-zinc-800 border border-white/10 text-zinc-500 hover:text-white"
              }`}
            >
              {popupEnabled ? "ON" : "OFF"}
            </button>
          </div>
          <div className="border border-white/10 p-5 flex items-center justify-between">
            <div>
              <h3 className="font-subheading text-sm uppercase tracking-wider">Drive Sync</h3>
              <p className="font-body text-xs text-zinc-500 mt-1">Pull veteran photos</p>
            </div>
            <button
              data-testid="sync-drive-btn"
              onClick={async () => {
                try {
                  toast.info("Syncing from Google Drive...");
                  const res = await axios.post(`${API}/admin/sync-drive`, {}, { headers: getHeaders() });
                  toast.success(res.data.message);
                  fetchData();
                } catch { toast.error("Sync failed"); }
              }}
              className="font-subheading text-xs uppercase tracking-widest px-5 py-2.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-all duration-300"
            >
              Sync
            </button>
          </div>
          <div className="border border-white/10 p-5 flex items-center justify-between">
            <div>
              <h3 className="font-subheading text-sm uppercase tracking-wider">YouTube Sync</h3>
              <p className="font-body text-xs text-zinc-500 mt-1">Pull new videos</p>
            </div>
            <button
              data-testid="sync-youtube-btn"
              onClick={async () => {
                try {
                  toast.info("Syncing from YouTube...");
                  const res = await axios.post(`${API}/admin/sync-youtube`, {}, { headers: getHeaders() });
                  toast.success(res.data.message);
                  fetchData();
                } catch { toast.error("Sync failed"); }
              }}
              className="font-subheading text-xs uppercase tracking-widest px-5 py-2.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all duration-300"
            >
              Sync
            </button>
          </div>
        </div>

        {/* ── SPONSORSHIP TIERS MANAGEMENT ── */}
        <SponsorshipManager />

        {/* Promotions Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-subheading text-2xl uppercase tracking-tight">Promotions</h2>
          <button
            data-testid="add-promotion-btn"
            onClick={() => { setShowForm(true); setEditId(null); setForm({ title: "", description: "", image_url: "", event_date: "", popup_duration: 8, support_url: "", active: true }); }}
            className="flex items-center gap-2 font-subheading text-xs uppercase tracking-widest bg-white text-black px-5 py-2.5 hover:bg-zinc-200 transition-colors"
          >
            <Plus size={14} /> Add Promotion
          </button>
        </div>

        {/* Promotion Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-white/10 p-8 mb-8"
          >
            <h3 className="font-subheading text-lg uppercase tracking-wider mb-6">
              {editId ? "Edit Promotion" : "New Promotion"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Title</label>
                  <input data-testid="promo-title" required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bc-input w-full" placeholder="Event title" />
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Event Date</label>
                  <input data-testid="promo-date" type="date" required value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} className="bc-input w-full" />
                </div>
              </div>
              <div>
                <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Description</label>
                <textarea data-testid="promo-desc" required rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bc-input w-full resize-none" placeholder="Event details..." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Image (Upload or URL)</label>
                  <input type="file" accept="image/*" onChange={async (e) => {
                    if (e.target.files[0]) {
                      try {
                        const url = await uploadImage(e.target.files[0]);
                        setForm({...form, image_url: url});
                        toast.success("Image uploaded");
                      } catch { toast.error("Upload failed"); }
                    }
                  }} className="bc-input w-full text-sm file:mr-3 file:border-0 file:bg-white/10 file:text-zinc-400 file:px-3 file:py-1" />
                  {form.image_url && <p className="font-body text-xs text-zinc-600 mt-1 truncate">{form.image_url}</p>}
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Support/Donate Link</label>
                  <input data-testid="promo-support-url" value={form.support_url} onChange={e => setForm({...form, support_url: e.target.value})} className="bc-input w-full" placeholder="https://..." />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Popup Duration (seconds)</label>
                  <input data-testid="promo-duration" type="number" min="3" max="30" value={form.popup_duration} onChange={e => setForm({...form, popup_duration: parseInt(e.target.value) || 8})} className="bc-input w-full" />
                </div>
                <div className="flex items-end pb-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} className="w-4 h-4" />
                    <span className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-400">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-4">
                <button type="submit" data-testid="promo-submit-btn" disabled={loading} className="font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-50">
                  {loading ? "Saving..." : editId ? "Update" : "Create"}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Upcoming Promotions */}
        <div className="mb-12">
          <h3 className="font-subheading text-lg uppercase tracking-wider text-zinc-400 mb-4 flex items-center gap-2">
            <Calendar size={16} /> Upcoming ({upcoming.length})
          </h3>
          {upcoming.length === 0 ? (
            <p className="font-body text-sm text-zinc-600">No upcoming promotions</p>
          ) : (
            <div className="space-y-3">
              {upcoming.map(p => (
                <div key={p.id} className="border border-white/10 p-5 flex items-center gap-4 card-hover">
                  {p.image_url && <img src={fixUrl(p.image_url)} alt="" className="w-16 h-16 object-cover shrink-0 border border-white/10" />}
                  {!p.image_url && <div className="w-16 h-16 border border-white/10 flex items-center justify-center shrink-0"><Image size={20} className="text-zinc-600" /></div>}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-subheading text-sm uppercase tracking-wider truncate">{p.title}</h4>
                    <p className="font-body text-xs text-zinc-500 truncate">{p.description}</p>
                    <p className="font-body text-xs text-zinc-600 mt-1">{p.event_date} &bull; Popup: {p.popup_duration}s</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleActive(p)} className="p-2 text-zinc-500 hover:text-white transition-colors" title={p.active ? "Deactivate" : "Activate"}>
                      {p.active ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    <button onClick={() => editPromo(p)} className="p-2 text-zinc-500 hover:text-white transition-colors"><Edit size={16} /></button>
                    <button onClick={() => deletePromo(p.id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Promotions */}
        <div>
          <h3 className="font-subheading text-lg uppercase tracking-wider text-zinc-600 mb-4">Past Promotions ({past.length})</h3>
          {past.length === 0 ? (
            <p className="font-body text-sm text-zinc-600">No past promotions</p>
          ) : (
            <div className="space-y-3 opacity-60">
              {past.map(p => (
                <div key={p.id} className="border border-white/5 p-5 flex items-center gap-4">
                  {p.image_url && <img src={fixUrl(p.image_url)} alt="" className="w-12 h-12 object-cover shrink-0 border border-white/5 grayscale" />}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-subheading text-sm uppercase tracking-wider truncate text-zinc-500">{p.title}</h4>
                    <p className="font-body text-xs text-zinc-600">{p.event_date}</p>
                  </div>
                  <button onClick={() => deletePromo(p.id)} className="p-2 text-zinc-600 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── PENDING TICKET APPROVALS ── */}
        {pendingTickets.length > 0 && (
          <div className="mb-12 border border-yellow-500/20 bg-yellow-500/5 p-6">
            <h2 className="font-subheading text-lg uppercase tracking-tight flex items-center gap-2 mb-4 text-yellow-400">
              <Ticket size={18} /> Pending Ticket Approvals ({pendingTickets.length})
            </h2>
            <div className="space-y-3">
              {pendingTickets.map(t => (
                <div key={t.id} className="border border-white/10 p-4 flex items-center gap-4 bg-black/30">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-subheading text-sm uppercase tracking-wider">{t.attendee_name}</h4>
                    <p className="font-body text-xs text-zinc-400">{t.attendee_email} &bull; {t.ticket_type} &bull; ${t.price?.toFixed(2)}</p>
                    <p className="font-body text-xs text-zinc-500">{t.event_title} &bull; via {t.payment_method?.toUpperCase()}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={async () => {
                      try {
                        await axios.post(`${API}/tickets/${t.id}/approve`, {}, { headers: getHeaders() });
                        toast.success(`Approved: ${t.attendee_name}`);
                        setPendingTickets(p => p.filter(x => x.id !== t.id));
                      } catch { toast.error("Failed"); }
                    }} className="flex items-center gap-1 px-4 py-2 text-xs font-subheading uppercase tracking-wider bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors">
                      <Check size={14} /> Approve
                    </button>
                    <button onClick={async () => {
                      if (!window.confirm("Reject this ticket?")) return;
                      try {
                        await axios.post(`${API}/tickets/${t.id}/reject`, {}, { headers: getHeaders() });
                        toast.success("Rejected");
                        setPendingTickets(p => p.filter(x => x.id !== t.id));
                      } catch { toast.error("Failed"); }
                    }} className="flex items-center gap-1 px-4 py-2 text-xs font-subheading uppercase tracking-wider bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors">
                      <Trash2 size={14} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── EVENTS MANAGEMENT ── */}
        <div className="mt-16 pt-12 border-t border-white/10">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-subheading text-2xl uppercase tracking-tight flex items-center gap-2">
              <Ticket size={20} /> Events
            </h2>
            <button
              data-testid="add-event-btn"
              onClick={() => { setShowEventForm(true); setEditEventId(null); setEventForm({ title: "", description: "", image_url: "", event_date: "", venue: "", ticket_types: "General Admission, VIP, Veteran (Free)", ticket_url: "", active: true }); }}
              className="flex items-center gap-2 font-subheading text-xs uppercase tracking-widest bg-white text-black px-5 py-2.5 hover:bg-zinc-200 transition-colors"
            >
              <Plus size={14} /> Add Event
            </button>
          </div>

          {showEventForm && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 p-8 mb-8">
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-6">{editEventId ? "Edit Event" : "New Event"}</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setLoading(true);
                const payload = { ...eventForm, ticket_types: eventForm.ticket_types.split(",").map(s => s.trim()).filter(Boolean) };
                try {
                  if (editEventId) {
                    await axios.put(`${API}/events/${editEventId}`, payload, { headers: getHeaders() });
                    toast.success("Event updated");
                  } else {
                    const res = await axios.post(`${API}/events`, payload, { headers: getHeaders() });
                    toast.success("Event created");
                    // Send notification if requested
                    if (eventForm.notify_subscribers && res.data.id) {
                      try {
                        const notifyRes = await axios.post(`${API}/events/${res.data.id}/notify`, {}, { headers: getHeaders() });
                        toast.success(`Notification sent to ${notifyRes.data.emails_count} subscribers. Social post generated.`);
                      } catch { toast.error("Event created but notification failed"); }
                    }
                  }
                  setShowEventForm(false); setEditEventId(null); fetchData();
                } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                setLoading(false);
              }} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Title</label>
                    <input data-testid="event-title" required value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} className="bc-input w-full" placeholder="Event name" />
                  </div>
                  <div>
                    <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Event Date</label>
                    <input data-testid="event-date" type="date" required value={eventForm.event_date} onChange={e => setEventForm({...eventForm, event_date: e.target.value})} className="bc-input w-full" />
                  </div>
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Description</label>
                  <textarea data-testid="event-desc" required rows={3} value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} className="bc-input w-full resize-none" placeholder="Event details..." />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Venue</label>
                    <input value={eventForm.venue} onChange={e => setEventForm({...eventForm, venue: e.target.value})} className="bc-input w-full" placeholder="Location" />
                  </div>
                  <div>
                    <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Image (Upload or URL)</label>
                    <input type="file" accept="image/*" onChange={async (e) => {
                      if (e.target.files[0]) {
                        try {
                          const url = await uploadImage(e.target.files[0]);
                          setEventForm({...eventForm, image_url: url});
                          toast.success("Image uploaded");
                        } catch { toast.error("Upload failed"); }
                      }
                    }} className="bc-input w-full text-sm file:mr-3 file:border-0 file:bg-white/10 file:text-zinc-400 file:px-3 file:py-1" />
                    <input value={eventForm.image_url} onChange={e => setEventForm({...eventForm, image_url: e.target.value})} className="bc-input w-full mt-2" placeholder="Or paste image URL..." />
                  </div>
                </div>
                <div>
                  <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Ticket Types (comma-separated)</label>
                  <input value={eventForm.ticket_types} onChange={e => setEventForm({...eventForm, ticket_types: e.target.value})} className="bc-input w-full" placeholder="General Admission, VIP, Veteran (Free)" />
                </div>
                {!editEventId && (
                  <div className="border border-yellow-500/20 bg-yellow-500/5 p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={eventForm.notify_subscribers} onChange={e => setEventForm({...eventForm, notify_subscribers: e.target.checked})} className="w-4 h-4" />
                      <div>
                        <span className="font-subheading text-xs uppercase tracking-[0.15em] text-yellow-400">Send notification to all subscribers</span>
                        <p className="font-body text-xs text-zinc-500 mt-0.5">Emails subscribers and generates a social media post</p>
                      </div>
                    </label>
                  </div>
                )}
                <div className="flex gap-4">
                  <button type="submit" disabled={loading} className="font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-50">
                    {loading ? "Saving..." : editEventId ? "Update" : "Create"}
                  </button>
                  <button type="button" onClick={() => setShowEventForm(false)} className="font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white transition-colors">Cancel</button>
                </div>
              </form>
            </motion.div>
          )}

          {/* RSVP Viewer */}
          {rsvpView && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border border-white/10 p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-subheading text-sm uppercase tracking-wider">RSVPs for: {rsvpView.title}</h4>
                <button onClick={() => setRsvpView(null)} className="text-zinc-500 hover:text-white text-xs">Close</button>
              </div>
              {rsvps.length === 0 ? (
                <p className="font-body text-sm text-zinc-600">No RSVPs yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {rsvps.map(r => (
                    <div key={r.id} className="flex items-center justify-between border border-white/5 p-3">
                      <div>
                        <span className="font-body text-sm text-zinc-300">{r.email}</span>
                        {r.name && <span className="font-body text-xs text-zinc-500 ml-2">({r.name})</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {r.ticket_preferences.map(t => (
                          <span key={t} className="font-body text-[10px] text-zinc-600 border border-white/5 px-2 py-0.5">{t}</span>
                        ))}
                        {r.notified && <Bell size={12} className="text-green-500" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Events List */}
          <div className="space-y-3">
            {events.map(ev => {
              const isPast = ev.event_date < new Date().toISOString().slice(0, 10);
              return (
                <div key={ev.id} className={`border p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${isPast ? 'border-white/5 opacity-50' : !ev.active ? 'border-yellow-500/20 opacity-60' : 'border-white/10 card-hover'}`}>
                  {ev.image_url && <img src={fixUrl(ev.image_url)} alt="" className={`w-16 h-16 object-cover shrink-0 border border-white/10 ${isPast || !ev.active ? 'grayscale' : ''}`} />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-subheading text-sm uppercase tracking-wider truncate">{ev.title}</h4>
                      {!ev.active && <span className="font-body text-[10px] text-yellow-500 border border-yellow-500/20 px-2 py-0.5">HIDDEN</span>}
                    </div>
                    <p className="font-body text-xs text-zinc-500">{ev.event_date} {ev.venue && `- ${ev.venue}`}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="font-body text-xs text-zinc-600 flex items-center gap-1"><Users size={10} /> {ev.rsvp_count || 0} RSVPs</span>
                      {ev.tickets_available && <span className="font-body text-[10px] text-green-500 border border-green-500/20 px-2 py-0.5">TICKETS LIVE</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <button onClick={async () => {
                      setRsvpView(ev);
                      const res = await axios.get(`${API}/rsvps/${ev.id}`, { headers: getHeaders() });
                      setRsvps(res.data);
                    }} className="p-2 text-zinc-500 hover:text-white transition-colors" title="View RSVPs"><Users size={16} /></button>
                    {!ev.tickets_available && !isPast && (
                      <button onClick={async () => {
                        if (!window.confirm("Mark tickets as available? This will notify all RSVPs that tickets are now live.")) return;
                        try {
                          const res = await axios.post(`${API}/events/${ev.id}/release-tickets`, {}, { headers: getHeaders() });
                          toast.success(res.data.message);
                          fetchData();
                        } catch { toast.error("Failed"); }
                      }} className="flex items-center gap-1 px-3 py-1.5 text-xs font-subheading uppercase tracking-wider bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors" title="Mark tickets as available and notify RSVPs">
                        <Send size={12} /> Release Tickets
                      </button>
                    )}
                    {!isPast && (
                      <button onClick={async () => {
                        if (!window.confirm("Send notification to all subscribers about this event?")) return;
                        try {
                          const res = await axios.post(`${API}/events/${ev.id}/notify`, {}, { headers: getHeaders() });
                          toast.success(res.data.message);
                        } catch { toast.error("Notification failed"); }
                      }} className="flex items-center gap-1 px-3 py-1.5 text-xs font-subheading uppercase tracking-wider bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-colors" title="Notify Subscribers">
                        <Bell size={12} /> Notify
                      </button>
                    )}
                    <button onClick={async () => {
                      try {
                        await axios.put(`${API}/events/${ev.id}`, { active: !ev.active }, { headers: getHeaders() });
                        toast.success(ev.active ? "Event hidden" : "Event visible");
                        fetchData();
                      } catch { toast.error("Failed"); }
                    }} className="p-2 text-zinc-500 hover:text-white transition-colors" title={ev.active ? "Hide event" : "Show event"}>
                      {ev.active ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    <button onClick={() => {
                      setEventForm({ title: ev.title, description: ev.description, image_url: ev.image_url, event_date: ev.event_date, venue: ev.venue, ticket_types: (ev.ticket_types || []).join(", "), ticket_url: ev.ticket_url || "", active: ev.active, notify_subscribers: false });
                      setEditEventId(ev.id); setShowEventForm(true);
                    }} className="p-2 text-zinc-500 hover:text-white transition-colors"><Edit size={16} /></button>
                    <button onClick={async () => {
                      if (!window.confirm("Delete this event and all RSVPs?")) return;
                      await axios.delete(`${API}/events/${ev.id}`, { headers: getHeaders() });
                      toast.success("Deleted"); fetchData();
                    }} className="p-2 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
              );
            })}
            {events.length === 0 && <p className="font-body text-sm text-zinc-600">No events yet</p>}
          </div>
        </div>

        {/* ── DATABASE VIEWER ── */}
        <DatabaseViewer />
      </div>
    </div>
  );
}

// ── Database Viewer Component ──
function DatabaseViewer() {
  const [activeTab, setActiveTab] = useState("subscribers");
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const tabs = [
    { id: "subscribers", label: "Subscribers" },
    { id: "contacts", label: "Messages" },
    { id: "nominations", label: "Nominations" },
    { id: "donors", label: "Donors" },
  ];

  const fetchTabData = useCallback(async (tab) => {
    setLoadingData(true);
    try {
      const res = await axios.get(`${API}/${tab}`, { headers: getHeaders() });
      setData(res.data);
    } catch { setData([]); }
    setLoadingData(false);
  }, []);

  useEffect(() => { fetchTabData(activeTab); }, [activeTab, fetchTabData]);

  const exportCSV = () => {
    if (data.length === 0) return;
    const keys = Object.keys(data[0]).filter(k => k !== "id");
    const csv = [keys.join(","), ...data.map(row => keys.map(k => `"${(row[k] || '').toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${activeTab}_export.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported to CSV");
  };

  return (
    <div className="mt-16 pt-12 border-t border-white/10">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-subheading text-2xl uppercase tracking-tight flex items-center gap-2">
          <Users size={20} /> Database
        </h2>
        <button onClick={exportCSV} className="flex items-center gap-2 font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-5 py-2.5 hover:border-white hover:bg-white/5 transition-all">
          <Copy size={12} /> Export CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/10">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`db-tab-${tab.id}`}
            className={`font-subheading text-xs uppercase tracking-wider px-5 py-3 border-b-2 transition-colors ${
              activeTab === tab.id
                ? "text-white border-white"
                : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Data Table */}
      {loadingData ? (
        <div className="py-12 text-center"><p className="font-body text-sm text-zinc-500">Loading...</p></div>
      ) : data.length === 0 ? (
        <div className="py-12 text-center"><p className="font-body text-sm text-zinc-600">No records yet</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {activeTab === "subscribers" && (
                  <><th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Email</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Name</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Status</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Date</th></>
                )}
                {activeTab === "contacts" && (
                  <><th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Name</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Email</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Message</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Date</th></>
                )}
                {activeTab === "nominations" && (
                  <><th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Veteran</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Nominator</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Story</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Date</th></>
                )}
                {activeTab === "donors" && (
                  <><th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Name</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Email</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Amount</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Message</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Date</th>
                  <th className="text-left p-3 font-subheading text-xs uppercase tracking-wider text-zinc-500">Receipt</th></>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={row.id || i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  {activeTab === "subscribers" && (
                    <><td className="p-3 font-body text-sm text-zinc-300">{row.email}</td>
                    <td className="p-3 font-body text-sm text-zinc-400">{row.name || "-"}</td>
                    <td className="p-3"><span className={`font-body text-[10px] uppercase px-2 py-0.5 ${row.status === 'active' ? 'text-green-400 border border-green-500/20' : 'text-zinc-500 border border-white/10'}`}>{row.status}</span></td>
                    <td className="p-3 font-body text-xs text-zinc-600">{row.subscribed_at?.slice(0, 10)}</td></>
                  )}
                  {activeTab === "contacts" && (
                    <><td className="p-3 font-body text-sm text-zinc-300">{row.name}</td>
                    <td className="p-3 font-body text-sm text-zinc-400">{row.email}</td>
                    <td className="p-3 font-body text-sm text-zinc-500 max-w-xs truncate">{row.message}</td>
                    <td className="p-3 font-body text-xs text-zinc-600">{row.created_at?.slice(0, 10)}</td></>
                  )}
                  {activeTab === "nominations" && (
                    <><td className="p-3 font-body text-sm text-zinc-300">{row.veteran_name}</td>
                    <td className="p-3 font-body text-sm text-zinc-400">{row.nominator_name} ({row.nominator_email})</td>
                    <td className="p-3 font-body text-sm text-zinc-500 max-w-xs truncate">{row.veteran_story}</td>
                    <td className="p-3 font-body text-xs text-zinc-600">{row.created_at?.slice(0, 10)}</td></>
                  )}
                  {activeTab === "donors" && (
                    <><td className="p-3 font-body text-sm text-zinc-300">{row.name}</td>
                    <td className="p-3 font-body text-sm text-zinc-400">{row.email}</td>
                    <td className="p-3 font-body text-sm text-zinc-300">{row.amount ? `$${row.amount}` : "-"}</td>
                    <td className="p-3 font-body text-sm text-zinc-500 max-w-xs truncate">{row.message || "-"}</td>
                    <td className="p-3 font-body text-xs text-zinc-600">{row.created_at?.slice(0, 10)}</td>
                    <td className="p-3"><button onClick={() => window.open(`${API}/donors/${row.id}/receipt`, '_blank')} className="font-body text-[10px] text-blue-400 border border-blue-500/20 px-2 py-0.5 hover:bg-blue-500/10 transition-colors">Receipt</button></td></>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="font-body text-xs text-zinc-600 mt-4">{data.length} total records</p>
        </div>
      )}
    </div>
  );
}
